import { useState } from "react";
import styled from "styled-components";
import { tokens, Page, Meta, TextButton } from "../styles";
import BibBlock from "./BibBlock";

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
`;
const Entry = styled.article`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;
const Title = styled.a`
  align-self: flex-start;
  font-family: ${tokens.serif};
  font-size: 19px;
  font-weight: 500;
  line-height: 1.35;
  letter-spacing: -0.01em;
`;
const Oral = styled.span`
  color: ${tokens.blue};
  margin-left: 8px;
`;
const Links = styled.div`
  display: flex;
  gap: 14px;
  font-size: 13px;
  margin-top: 2px;
`;
const Bib = styled(BibBlock)`
  margin-top: 8px;
`;
const Footnote = styled.p`
  && {
    margin-top: 64px;
    font-family: ${tokens.serif};
    font-style: italic;
    font-size: 16px;
    color: ${tokens.soft};
  }
`;

const ext = { target: "_blank", rel: "noopener noreferrer" };

const papers = [
  {
    title: "Cancer-Net PCa-MultiSeg",
    desc: "Improving prostate lesion segmentation without acquisition protocol changes in hospitals.",
    venue: "ML4H Findings, 2025",
    paper: "https://arxiv.org/abs/2511.07816",
    code: "https://github.com/Jdewbury/Cancer-Net-PCa-MultiSeg",
    bibtex: `@misc{dewbury2025cancernetpcamultisegmultimodalenhancement,
  title={Cancer-Net PCa-MultiSeg: Multimodal Enhancement of Prostate Cancer Lesion Segmentation Using Synthetic Correlated Diffusion Imaging},
  author={Jarett Dewbury and Chi-en Amy Tai and Alexander Wong},
  year={2025}, eprint={2511.07816}, archivePrefix={arXiv}, primaryClass={cs.CV},
  url={https://arxiv.org/abs/2511.07816},
}`,
  },
  {
    title: "Cancer-Net PCa-Seg",
    desc: "Benchmarking segmentation models on synthetic correlated diffusion MRI.",
    venue: "AAAI Workshop on Health Intelligence, 2025",
    paper: "https://arxiv.org/abs/2501.09185",
    code: "https://github.com/Jdewbury/Cancer-Net-PCa-MultiSeg/tree/Cancer-Net-PCa-Seg-v1.0.0",
    bibtex: `@misc{dewbury2025cancernetpcasegbenchmarkingdeep,
  title={Cancer-Net PCa-Seg: Benchmarking Deep Learning Models for Prostate Cancer Segmentation Using Synthetic Correlated Diffusion Imaging},
  author={Jarett Dewbury and Chi-en Amy Tai and Alexander Wong},
  year={2025}, eprint={2501.09185}, archivePrefix={arXiv}, primaryClass={eess.IV},
  url={https://arxiv.org/abs/2501.09185},
}`,
  },
  {
    title: "Sharpness-Aware Minimization for Raman diagnostics",
    desc: "Flat-minima training for bacterial Raman classification.",
    venue: "ICLR Workshop on Practical ML for Low Resource Settings, 2024",
    oral: true,
    paper: "https://arxiv.org/pdf/2609.19453",
    code: "https://github.com/Tadesse-Lab/SAM-Raman-Diagnostics",
    slides: "/slides/SPIE_2025_SAM_Raman_Oral_Talk.pdf",
    bibtex: `@inproceedings{zareno2024sharpnessaware,
  title={Sharpness-Aware Minimization (SAM) Improves Classification Accuracy of Bacterial Raman Spectral Data Enabling Portable Diagnostics},
  author={Kaitlin Zareno and Jarett Dewbury and Siamak Sorooshyari and Hossein Mobahi and Loza F. Tadesse},
  booktitle={5th Workshop on practical ML for limited/low resource settings},
  year={2024}, url={https://arxiv.org/pdf/2609.19453}
}`,
  },
];

export default function Research() {
  const [open, setOpen] = useState(null);

  return (
    <Page>
      <List>
        {papers.map((p, i) => (
          <Entry key={p.title}>
            <Title href={p.paper} {...ext}>{p.title}</Title>
            <p>{p.desc}</p>
            <Meta>
              {p.venue}
              {p.oral && <Oral>oral</Oral>}
            </Meta>
            <Links>
              <a href={p.paper} {...ext}>paper</a>
              {p.code && <a href={p.code} {...ext}>code</a>}
              {p.slides && <a href={p.slides} {...ext}>slides</a>}
              <TextButton
                type="button"
                $active={open === i}
                aria-expanded={open === i}
                onClick={() => setOpen(open === i ? null : i)}
              >
                bibtex
              </TextButton>
            </Links>
            {open === i && <Bib bibtex={p.bibtex} />}
          </Entry>
        ))}
      </List>

      <Footnote>
        The full list of my publications is on{" "}
        <a href="https://scholar.google.com/citations?user=coiG25kAAAAJ&hl=en" {...ext}>Google Scholar</a>.
      </Footnote>
    </Page>
  );
}